import { useState, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { getApiErrorInfo } from '../../services/api';
import type { CourseSyncCourse, CourseSyncResult } from '../../services/courseSyncService';

const OUTCOME_COLORS: Record<string, 'success' | 'warning' | 'default'> = {
  Added: 'success',
  Updated: 'warning',
  Unchanged: 'default',
};

interface CourseImportSyncProps {
  syncAction: () => Promise<CourseSyncResult>;
  loadingMessage: string;
  pulledLabel?: string;
  renderSummaryExtras?: (result: CourseSyncResult) => ReactNode;
  renderTableHeadExtras?: () => ReactNode;
  renderTableRowExtras?: (course: CourseSyncCourse) => ReactNode;
}

export default function CourseImportSync({
  syncAction,
  loadingMessage,
  pulledLabel = 'Pulled',
  renderSummaryExtras,
  renderTableHeadExtras,
  renderTableRowExtras,
}: CourseImportSyncProps) {
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CourseSyncResult | null>(null);

  async function handleSync() {
    setSyncing(true);
    setError(null);
    setResult(null);
    try {
      const data = await syncAction();
      setResult(data);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Sync failed. Ensure the scheduled functions app is running and try again.');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Run Sync'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          <Typography variant="body2" color="text.secondary">
            Sync complete:
          </Typography>
          <Chip label={`${result.pulledCount} ${pulledLabel}`} size="small" />
          {renderSummaryExtras?.(result)}
          <Chip label={`${result.addedCount} Added`} color="success" size="small" />
          <Chip label={`${result.updatedCount} Updated`} color="warning" size="small" />
          <Chip label={`${result.unchangedCount} Unchanged`} size="small" />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {result.processedCount} processed
          </Typography>
        </Paper>
      )}

      {result && result.courses.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Course ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Outcome</TableCell>
                {renderTableHeadExtras?.()}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.courses.map((course) => (
                <TableRow key={course.sourceSystemCourseId} hover>
                  <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                    {course.sourceSystemCourseId}
                  </TableCell>
                  <TableCell>{course.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={course.outcome}
                      color={OUTCOME_COLORS[course.outcome] ?? 'default'}
                      size="small"
                    />
                  </TableCell>
                  {renderTableRowExtras?.(course)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {syncing && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <CircularProgress size={36} sx={{ mb: 2 }} />
          <Typography>{loadingMessage}</Typography>
        </Paper>
      )}

      {result && result.addedCount === 0 && result.updatedCount === 0 && (
        <Alert severity="success" sx={{ mt: 1 }}>
          All {result.unchangedCount} courses are already up to date.
        </Alert>
      )}
    </Box>
  );
}